import React, { useEffect, useState } from "react";
import { Download, Lock, Mail, MessageCircle, Paperclip, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import TemplateEditor from "./TemplateEditor";

const EMPTY = { name: "Nouveau modèle", kind: "libre", role: "", partner: "", docs: [], attachments: [], email_subject: "", email_body: "Bonjour {destinataire.nom},\n\n\n\nCordialement,\nL'équipe Norea", whatsapp: "", order: 50 };

function TemplateRow({ t, meta, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border border-norea-border rounded-md hover:border-norea-primary/40 transition-colors" data-testid={`tpl-row-${t.id}`}>
      <button onClick={onEdit} className="flex-1 min-w-0 text-left" data-testid={`tpl-edit-${t.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-norea-text">{t.name}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-norea-border text-norea-textMuted inline-flex items-center gap-1"><Users className="w-2.5 h-2.5" />{meta.roles[t.role] || "Tous"}</span>
          {t.partner && <span className="text-[10px] px-1.5 py-0.5 rounded border border-norea-primary/40 text-norea-primary" data-testid={`tpl-partner-${t.id}`}>{t.partner}</span>}
          {t.email_body && <Mail className="w-3 h-3 text-norea-textMuted" />}
          {t.whatsapp && <MessageCircle className="w-3 h-3 text-emerald-600" />}
          {t.attachments?.length > 0 && <span className="text-[10px] text-norea-textMuted inline-flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{t.attachments.length}</span>}
        </div>
        <div className="text-[11px] text-norea-textMuted truncate mt-0.5">{(t.email_subject || t.whatsapp || t.email_body || "").slice(0, 140)}</div>
      </button>
      {t.system ? <span className="p-1.5 text-norea-textDisabled" title="Modèle de base : modifiable, pas supprimable" data-testid={`tpl-locked-${t.id}`}><Lock className="w-3.5 h-3.5" /></span>
        : <button onClick={onDelete} className="p-1.5 text-norea-textMuted hover:text-status-erreurText" data-testid={`tpl-delete-${t.id}`} aria-label="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

export default function MessageTemplatesPanel() {
  const [data, setData] = useState(null);
  const [families, setFamilies] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/message-templates").then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); api.get("/families", { params: { partner: true } }).then((r) => setFamilies(r.data.items || [])).catch(() => {}); }, []);
  const remove = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try { await api.delete(`/message-templates/${t.id}`); toast.success("Modèle supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const exportPdf = async () => {
    try {
      const r = await api.get("/message-templates/export.pdf", { params: { kinds: "sav,desinstallation" }, responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = "norea-textes-emails-a-valider.pdf"; a.click(); setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch { toast.error("Export impossible"); }
  };
  if (!data) return <div className="h-40 bg-white border border-norea-border rounded-md animate-pulse" />;
  return (
    <div className="card-flat p-6" data-testid="panel-message-templates">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-xl font-medium text-norea-text">Modèles de messages</h3>
          <p className="text-xs text-norea-textMuted mt-1 max-w-2xl">Emails et WhatsApp pré-remplis avec les variables du dossier ({"{client.nom}"}, {"{installation.orientation}"}…, y compris vos champs ajoutés). Un modèle peut être réservé à un partenaire précis et proposer des pièces jointes par défaut. Les variables vides sont signalées avant l'envoi.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={exportPdf} className="btn-secondary" data-testid="tpl-export-pdf"><Download className="w-4 h-4" /> Textes SAV & désinstallation (PDF)</button>
          <button onClick={() => setEditing(EMPTY)} className="btn-secondary" data-testid="tpl-add"><Plus className="w-4 h-4" /> Nouveau modèle</button>
        </div>
      </div>
      <div className="space-y-6">
        {Object.entries(data.kinds).map(([k, label]) => (
          <section key={k} data-testid={`tpl-group-${k}`}>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-norea-textMuted mb-2">{label}</div>
            <div className="space-y-2">
              {data.items.filter((t) => t.kind === k).map((t) => <TemplateRow key={t.id} t={t} meta={data} onEdit={() => setEditing(t)} onDelete={() => remove(t)} />)}
            </div>
          </section>
        ))}
      </div>
      {editing && <TemplateEditor template={editing} meta={data} families={families} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
